import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, ScrollView, Switch } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '../../../src/theme/ThemeContext';
import { useThemedStyles } from '../../../src/theme/useThemedStyles';
import Icon, { ICON_SIZES } from '../../../src/components/Icon';
import type { Theme } from '../../../src/theme/types';
import { useAiSettingsStore } from '../../../src/stores/aiSettingsStore';
import { settingsApi } from '../../../src/api/client';
import { showAlert, showConfirm } from '../../../src/utils/alert';
import { requireConnectivity } from '../../../src/utils/connectivity';
import { PROMPT_CATEGORIES } from '../../../src/services/llm.promptRegistry';
import ScreenHeader from '../../../src/components/ScreenHeader';

const createStyles = (theme: Theme) => ({
  container: { flex: 1, backgroundColor: theme.colors.bgSecondary },
  content: { padding: 16, paddingBottom: 40, gap: 24 },
  sectionTitle: {
    fontSize: theme.fs(14),
    fontWeight: '700' as const,
    color: theme.colors.textMuted,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    marginBottom: 10,
    paddingHorizontal: 4,
  },
  card: {
    backgroundColor: theme.colors.background,
    borderRadius: 12,
    overflow: 'hidden' as const,
    ...theme.shadows.sm,
  },
  switchRow: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
    padding: 16,
  },
  switchLabelContainer: { flex: 1, marginRight: 12 },
  settingLabel: { fontSize: theme.fs(15), fontWeight: '600' as const, color: theme.colors.textPrimary },
  settingDescription: { fontSize: theme.fs(13), color: theme.colors.textMuted, marginTop: 4, lineHeight: theme.fs(18) },
  divider: { height: 1, backgroundColor: theme.colors.bgTertiary, marginHorizontal: 16 },
  categoryRow: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
    padding: 16,
  },
  categoryLeft: { flex: 1 },
  categoryLabel: { fontSize: theme.fs(15), fontWeight: '500' as const, color: theme.colors.textPrimary },
  categoryBadge: { fontSize: theme.fs(12), color: theme.colors.textMuted, marginTop: 2 },
  customBadge: { color: theme.colors.primary },
  disabledRow: { opacity: 0.4 },
});

export default function AiFeaturesScreen() {
  const router = useRouter();
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();

  const aiEnabled = useAiSettingsStore((s) => s.aiEnabled);
  const aiAcknowledged = useAiSettingsStore((s) => s.aiAcknowledged);
  const customPrompts = useAiSettingsStore((s) => s.customPrompts);
  const setAiEnabledStore = useAiSettingsStore((s) => s.setAiEnabled);
  const [isSaving, setIsSaving] = useState(false);

  // Load settings on mount if store not initialized
  const isInitialized = useAiSettingsStore((s) => s.isInitialized);
  useEffect(() => {
    if (!isInitialized) {
      settingsApi.get().then((res) => {
        useAiSettingsStore.getState().loadFromSettings(res.data);
      }).catch(() => {});
    }
  }, [isInitialized]);

  const enableAi = async () => {
    if (!requireConnectivity()) return;
    const prev = aiEnabled;
    setAiEnabledStore(true);
    setIsSaving(true);
    try {
      const updateData: { ai_enabled: boolean; ai_acknowledged?: boolean } = { ai_enabled: true };
      if (!aiAcknowledged) {
        updateData.ai_acknowledged = true;
      }
      const response = await settingsApi.update(updateData);
      useAiSettingsStore.getState().loadFromSettings(response.data);
    } catch {
      setAiEnabledStore(prev);
      showAlert('Error', 'Failed to update setting.');
    } finally {
      setIsSaving(false);
    }
  };

  const disableAi = async () => {
    if (!requireConnectivity()) return;
    const prev = aiEnabled;
    setAiEnabledStore(false);
    setIsSaving(true);
    try {
      const response = await settingsApi.update({ ai_enabled: false });
      useAiSettingsStore.getState().loadFromSettings(response.data);
    } catch {
      setAiEnabledStore(prev);
      showAlert('Error', 'Failed to update setting.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleAi = (value: boolean) => {
    if (!value) {
      disableAi();
      return;
    }

    // Show acknowledgement dialog on first enable only
    if (!aiAcknowledged) {
      showConfirm(
        'Enable AI Features?',
        'AI suggestions are generated on-device and may not always be accurate. Please review all suggestions before applying them.',
        enableAi,
        { confirmText: 'I Understand, Enable' },
      );
    } else {
      enableAi();
    }
  };

  const hasCustomPrompts = (keys: string[]): boolean => {
    if (!customPrompts) return false;
    return keys.some((k) => customPrompts[k] != null);
  };

  return (
    <View style={styles.container}>
      <ScreenHeader
        title="AI Features"
        left={{
          label: 'Go back',
          icon: 'arrow-back',
          onPress: () => router.back(),
          testID: 'ai-features.back-button',
        }}
      />
      <ScrollView contentContainerStyle={styles.content}>
        {/* Master Toggle */}
        <View>
          <Text style={styles.sectionTitle} accessibilityRole="header">General</Text>
          <View style={styles.card}>
            <TouchableOpacity activeOpacity={0.7} onPress={() => handleToggleAi(!aiEnabled)} style={styles.switchRow} accessible={true} accessibilityRole="switch" accessibilityLabel="Enable AI Features" accessibilityHint="Use on-device AI for suggestions, validation, coverage analysis, and reflections" accessibilityState={{ checked: aiEnabled }} testID="ai-features.enable-switch">
              <View style={styles.switchLabelContainer}>
                <Text style={styles.settingLabel} accessible={false}>Enable AI Features</Text>
                <Text style={styles.settingDescription} accessible={false}>
                  Use on-device AI for suggestions, validation, coverage analysis, and reflections
                </Text>
              </View>
              <Switch
                value={aiEnabled}
                onValueChange={handleToggleAi}
                disabled={isSaving}
                trackColor={{ false: colors.border, true: colors.primaryLight }}
                thumbColor={aiEnabled ? colors.primary : colors.bgSecondary}
                accessibilityLabel="Enable AI Features"
              />
            </TouchableOpacity>
          </View>
        </View>

        {/* Prompt Categories */}
        <View>
          <Text style={styles.sectionTitle} accessibilityRole="header">Prompt Customization</Text>
          <View style={styles.card}>
            {PROMPT_CATEGORIES.map((cat, index) => {
              const isCustom = hasCustomPrompts(cat.keys);
              return (
                <React.Fragment key={cat.label}>
                  {index > 0 && <View style={styles.divider} />}
                  <TouchableOpacity
                    style={[styles.categoryRow, !aiEnabled && styles.disabledRow]}
                    disabled={!aiEnabled}
                    onPress={() =>
                      router.push({
                        pathname: '/settings/prompt-editor',
                        params: { category: cat.label, keys: JSON.stringify(cat.keys) },
                      } as any)
                    }
                    accessibilityLabel={`${cat.label}${isCustom ? ', customized' : ', default'}`}
                    accessibilityRole="button"
                    testID={`ai-features.category-${cat.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <View style={styles.categoryLeft} accessible={false}>
                      <Text style={styles.categoryLabel} accessible={false}>{cat.label}</Text>
                      <Text style={[styles.categoryBadge, isCustom && styles.customBadge]} accessible={false}>
                        {isCustom ? 'Customized' : 'Default'}
                      </Text>
                    </View>
                    <Icon name="navigate" size={ICON_SIZES.inline} color={colors.textMuted} />
                  </TouchableOpacity>
                </React.Fragment>
              );
            })}
          </View>
        </View>
      </ScrollView>
    </View>
  );
}
